/**
 * Auxiliary functions
 */
const replaceSquareBracketsInString = (str) => {
  const pattern1 = /\[.*?\]/g;
  const pattern2 = /\[|\]/g;
  return str.replace(pattern1, (content) => {
    const newContent = content.replace(pattern2, '');
    return `.${newContent}`;
  }).replace(/^[.]?/, ''); // At last replace the first character if its a point
};

/**
 * Lodash replacement functions
 */
export const isEmptyString = (str) => !str?.trim();
export const isEmptyArray = (arr) => !arr?.length;
export const isEmptyObject = (obj) => !obj || !Object.keys(obj).length;

export const isEmpty = (item) => {
  if (Array.isArray(item)) return isEmptyArray(item);
  if (typeof item === 'string') return isEmptyString(item);
  if (typeof item === 'object') return isEmptyObject(item);
  return true;
};

/**
 * Creates an object composed of the picked object properties.
 */
export const pick = (obj, paths) => {
  if (isEmptyObject(obj) || isEmptyArray(paths)) return {};

  const result = {};
  for (let i = 0; i < paths.length; i += 1) {
    const path = paths[i];
    if (path in obj) result[path] = obj[path];
  }
  return result;
};

/**
 * Gets the value at path of object. If the resolved value is undefined, the defaultValue is returned in its place.
 */
export const get = (obj, path, defaultValue) => {
  if (isEmptyObject(obj) || isEmptyString(path)) return defaultValue;

  const parsedPath = replaceSquareBracketsInString(path);
  const properties = parsedPath.split('.');
  let result = obj;

  for (let i = 0; i < properties.length; i += 1) {
    const prop = properties[i];
    if (result && result[prop] !== undefined) result = result[prop];
    else {
      result = defaultValue;
      break;
    }
  }
  return result;
};

export const flowRight = (...functions) => (init) => functions.reduceRight((firstFunc, secondFunc) => secondFunc(firstFunc), init);

/**
 * Checks if path is a direct property of object.
 */
export const has = (obj, path) => {
  if (isEmptyObject(obj) || isEmpty(path)) return false;

  const properties = typeof path === 'string' ? path.split('.') : path;
  let object = obj;

  for (let i = 0; i < properties.length; i += 1) {
    const prop = properties[i];
    if (typeof object === 'object' && prop in object) {
      object = object[prop];
    } else { return false; }
  }
  return true;
};

export const identity = (value) => value;

export const omit = (obj, paths) => {
  if (isEmptyObject(obj) || isEmptyArray(paths)) return {};

  const result = {};
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i += 1) {
    const path = keys[i];
    if (!paths.includes(path)) result[path] = obj[path];
  }
  return result;
};

function isObject(value) {
  const type = typeof value;
  return value != null && (type === 'object' || type === 'function');
}

/**
 * https://github.com/lodash/lodash/blob/4.17.15/lodash.js#L10304
 *
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
export function debounce(func, wait, options) {
  let lastArgs;
  let lastThis;
  let maxWait;
  let result;
  let timerId;
  let lastCallTime;
  let lastInvokeTime = 0;
  let leading = false;
  let maxing = false;
  let trailing = true;

  if (typeof func !== 'function') {
    throw new Error('debounce: func is not a function');
  }
  // eslint-disable-next-line no-param-reassign
  wait = parseInt(wait, 10) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? Math.max(parseInt(options.maxWait, 10) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    const args = lastArgs;
    const thisArg = lastThis;

    lastArgs = undefined;
    lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait)
      || (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = undefined;
    lastThis = undefined;
    return result;
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  // eslint-disable-next-line consistent-return
  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function cancel() {
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(Date.now());
  }

  function debounced() {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    // eslint-disable-next-line prefer-rest-params
    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

/**
 * Set Value in path of object.
 */
export const set = (obj, path, value) => {
  if (isEmpty(path)) return obj;

  const properties = typeof path === 'string' ? path.split('.') : path;
  let object = obj;

  for (let i = 0; i < properties.length - 1; i += 1) {
    const prop = properties[i];
    if (!object[prop] || !isObject(object[prop])) {
      object[prop] = {};
    }
    object = object[prop];
  }
  object[properties[properties.length - 1]] = value;

  return obj;
};
